import React from 'react';
import ReactDOM from 'react-dom/client';
import brandFavicon from './assets/branding/abogados_quiroz_compact.svg';
import axios from 'axios';
import './index.css';
import App from './App';
import './DarkMode.css';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { applyTheme } from './theme';

// A copied domain occasionally arrives as https://crmabogadosquiroz.com/.
// It is not an application route; canonicalise it before React Router restores
// authentication so the login and workspace always start from the real root.
if (window.location.pathname === '/.') {
  window.history.replaceState(null, '', `/${window.location.search}${window.location.hash}`);
}

// Apply the last personal palette before React paints to avoid a colour flash.
applyTheme(localStorage.getItem('qlf-theme'));

// The public HTML has a compatible fall-back icon for the initial paint.
// Replace it once React loads with the official compact Abogados Quiroz mark.
const favicon = document.getElementById('qlf-favicon');
if (favicon) {
  favicon.setAttribute('href', brandFavicon);
  favicon.setAttribute('type', 'image/svg+xml');
}

// La API se publica en la interfaz LAN para funcionar tanto desde 127.0.0.1
// como desde la dirección usada por el equipo (10.5.0.2).
// En localhost, OAuth debe iniciar y finalizar en la misma instancia del
// backend local. En la red LAN mantenemos la dirección compartida del CRM.
const crmHost = window.location.hostname;
const configuredApiRoot = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const configuredCrmApi = configuredApiRoot ? `${configuredApiRoot.replace(/\/api$/, '')}/api` : '';
const resolvedCrmApi = configuredCrmApi || ((crmHost === 'localhost' || crmHost === '127.0.0.1')
  ? 'http://localhost:8000/api'
  : `${window.location.origin}/api`);
window.__CRM_API__ = resolvedCrmApi;
axios.interceptors.request.use((config) => {
  const importedApiFallback = `${window.location.protocol}//${crmHost}:8000/api`;
  if (typeof config.url === 'string' && (config.url.startsWith('http://localhost:8000/api') || config.url.startsWith(importedApiFallback))) {
    // En algunos navegadores embebidos el global puede no estar disponible
    // durante el primer render. Nunca conviertas una URL en “undefined”.
    config.url = config.url
      .replace('http://localhost:8000/api', window.__CRM_API__ || resolvedCrmApi)
      .replace(importedApiFallback, window.__CRM_API__ || resolvedCrmApi);
  }
  return config;
});
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    // FastAPI validation failures are arrays/objects. Normalise them once so
    // every existing workspace can render a useful string instead of an
    // object or a misleading generic success notice.
    if (detail && typeof detail !== 'string') {
      const entries = Array.isArray(detail) ? detail : [detail];
      const messages = entries.map((item) => typeof item === 'string' ? item : item?.msg).filter(Boolean);
      const message = messages.join(' ') || 'Los datos ingresados no son válidos.';
      error.response.data.detail = message;
      error.userMessage = message;
    } else if (!detail && !error?.response) {
      error.userMessage = 'No fue posible comunicarse con el servidor. Verifique su conexión e intente nuevamente.';
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthProvider>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </AuthProvider>
  </React.StrictMode>
);
